// Source-of-truth content for SKV Digital Labs
import {
  TrendingUp, Megaphone, Sparkles, Users, BarChart3, GitBranch, Layers3, Target
} from 'lucide-react';

export const SERVICES = [
  {
    slug: 'performance-marketing',
    title: 'Performance Marketing',
    icon: TrendingUp,
    short: 'ROI-engineered paid acquisition across Google, Meta, LinkedIn, programmatic & retail media.',
    points: ['Full-funnel attribution', 'Creative testing systems', 'Bid & budget automation', 'CAC/LTV optimization'],
  },
  {
    slug: 'media-strategy',
    title: 'Media Strategy & Planning',
    icon: Megaphone,
    short: 'Channel-agnostic media architecture that allocates budget where compounding growth lives.',
    points: ['Audience & journey mapping', 'Channel mix modeling', 'Budget reallocation playbooks', 'Brand-to-performance balance'],
  },
  {
    slug: 'ai-marketing-automation',
    title: 'AI Marketing Automation',
    icon: Sparkles,
    short: 'Autonomous workflows that personalize, score, route, and convert at machine scale.',
    points: ['Lifecycle orchestration', 'Predictive segmentation', 'LLM-powered content ops', 'CRM + ad platform sync'],
  },
  {
    slug: 'customer-acquisition',
    title: 'Customer Acquisition',
    icon: Users,
    short: 'Repeatable, predictable pipelines purpose-built for ICP fit and unit economics.',
    points: ['ICP & offer design', 'Outbound + inbound stacks', 'Funnel diagnostics', 'Conversion engineering'],
  },
  {
    slug: 'analytics-growth-intelligence',
    title: 'Analytics & Growth Intelligence',
    icon: BarChart3,
    short: 'Decision-grade analytics: from event design to executive dashboards & MMM.',
    points: ['Modern data stack', 'Attribution + MMM', 'Cohort & retention analytics', 'KPI tree design'],
  },
  {
    slug: 'customer-journey-optimization',
    title: 'Customer Journey Optimization',
    icon: GitBranch,
    short: 'Friction-mapped journeys that lift activation, retention, and lifetime revenue.',
    points: ['Journey audits', 'CRO + UX experimentation', 'Onboarding redesign', 'Loyalty & winback systems'],
  },
  {
    slug: 'digital-transformation-consulting',
    title: 'Digital Transformation Consulting',
    icon: Layers3,
    short: 'Operating-model redesign that aligns people, process, data and AI to growth outcomes.',
    points: ['Maturity assessment', 'Capability roadmaps', 'Tech & data stack design', 'Change enablement'],
  },
  {
    slug: 'business-growth-strategy',
    title: 'Business Growth Strategy',
    icon: Target,
    short: 'C-suite growth strategy — markets, GTM, pricing, and category positioning.',
    points: ['Market entry playbooks', 'Pricing & packaging', 'GTM motions', 'Board-ready growth narratives'],
  },
];

export const INDUSTRIES = [
  'SaaS & Technology', 'D2C & E-commerce', 'Financial Services',
  'Healthcare & Pharma', 'EdTech & Education', 'Travel & Hospitality',
  'Real Estate & Proptech', 'Retail & Consumer',
];

export const REGIONS = [
  { name: 'United States', code: 'US' },
  { name: 'United Kingdom', code: 'UK' },
  { name: 'Europe', code: 'EU' },
  { name: 'UAE & Middle East', code: 'AE' },
  { name: 'Australia', code: 'AU' },
  { name: 'APAC', code: 'APAC' },
  { name: 'India', code: 'IN' },
  { name: 'Singapore', code: 'SG' },
];

export const WHY_US = [
  { title: 'AI-First Operating Model', desc: 'We don’t bolt AI on. Strategy, media, content, and analytics are re-architected around models, agents and data loops.' },
  { title: 'Outcome-Bound Engagements', desc: 'Mandates tied to CAC, LTV, pipeline velocity and EBITDA-impacting growth metrics — not vanity activity.' },
  { title: 'Senior, Operator-Led Teams', desc: 'No layered agencies. Every account is led by practitioners with P&L and platform-level experience.' },
  { title: 'Global Playbooks, Local Edge', desc: 'Cross-market patterns combined with on-ground cultural and regulatory fluency.' },
  { title: 'Data You Can Defend', desc: 'Server-side tracking, MMM, incrementality testing — measurement that survives privacy & boardroom scrutiny.' },
  { title: 'Speed With Rigor', desc: 'Two-week sprints, weekly experiment cadence, quarterly strategic recalibration.' },
];

export const TESTIMONIALS = [
  {
    quote: 'SKV rebuilt our acquisition engine end-to-end. We hit a 3.2x ROAS lift in 90 days and finally have a measurement stack we trust.',
    name: 'Priya Menon',
    title: 'VP Growth, FinTech Scale-up',
    region: 'Singapore',
  },
  {
    quote: 'Their AI automation roadmap collapsed our lead-to-opportunity cycle by 47%. The team operates like an internal C-suite extension.',
    name: 'James Whitford',
    title: 'CMO, Enterprise SaaS',
    region: 'London, UK',
  },
  {
    quote: 'We engaged SKV for performance marketing and got an entire growth operating system. Pipeline doubled in two quarters.',
    name: 'Aisha Al-Mansoori',
    title: 'Founder & CEO, D2C Brand',
    region: 'Dubai, UAE',
  },
  {
    quote: 'Sanjay and team brought clarity to a chaotic stack. Attribution, MMM, lifecycle — finally a single source of growth truth.',
    name: 'Daniel Reyes',
    title: 'Head of Marketing, HealthTech',
    region: 'New York, USA',
  },
  {
    quote: 'Rare combination of strategic depth and executional speed. They moved faster than our internal team — and smarter.',
    name: 'Marta Kowalski',
    title: 'Chief Digital Officer, Retail Group',
    region: 'Berlin, Germany',
  },
  {
    quote: 'They architected our entire customer journey and AI personalization. Retention curves flattened, LTV up 38%.',
    name: 'Rahul Iyer',
    title: 'COO, EdTech Unicorn',
    region: 'Bengaluru, India',
  },
];

export const FOUNDER = {
  name: 'Sanjay K Varma',
  title: 'Founder & Chief Growth Architect',
  experience: '15+ years',
  bio: [
    'Sanjay K Varma is the founder of SKV Digital Labs — a senior growth operator with 15+ years building, scaling and transforming marketing organizations across global markets.',
    'His work spans performance marketing, media architecture, AI-driven automation, and customer journey design for category-defining brands in SaaS, financial services, D2C, and enterprise technology.',
    'Sanjay has led growth mandates across the US, UK, Europe, UAE, APAC and India — translating boardroom ambition into measurable acquisition, retention and revenue outcomes.',
  ],
  philosophy: 'Growth is no longer a marketing problem — it is an operating-system problem. The companies that win the next decade will treat AI, data and customer experience as a single, compounding flywheel.',
};
